// src/components/ColaboradorDoMes/VotarColaborador.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../../firebaseConfig';
import { doc, getDoc, updateDoc, arrayUnion, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { FaTrophy, FaCheckCircle, FaExclamationTriangle, FaLock } from 'react-icons/fa';
import '../../styles/VotarColaborador.css';

const VotarColaborador = ({ userData }) => {
    const { surveyId } = useParams();
    const navigate = useNavigate();
    const [survey, setSurvey] = useState(null);
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hasVoted, setHasVoted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const fetchSurvey = async () => {
            try {
                const surveyRef = doc(db, 'Empresas', userData.empresa_id, 'employeeOfMonth', surveyId);
                const surveySnap = await getDoc(surveyRef);

                if (!surveySnap.exists()) {
                    setError("Votação não encontrada.");
                    setLoading(false);
                    return;
                }

                const data = surveySnap.data();
                
                // 1. Verifica se já votou
                if (data.votedUsers && data.votedUsers.includes(userData.id)) {
                    setHasVoted(true);
                }

                // 2. Verifica as regras de quem pode votar (Voter Scope)
                let canVote = true;
                if (data.voterRule === 'loja' && data.targetStore !== 'Todas') {
                    if (userData.loja !== data.targetStore) canVote = false;
                }
                if (data.voterRule === 'departamento' && data.targetDept !== 'Todos') {
                    if (userData.departamento !== data.targetDept) canVote = false;
                }

                if (!canVote) {
                    setError("Esta votação é restrita aos colaboradores da área/loja participante.");
                } else if (data.status !== 'active') {
                    setError("Esta votação já foi encerrada.");
                } else {
                    // Impede o usuário de votar nele mesmo (Opcional, mas recomendado)
                    const filteredCandidates = data.candidates.filter(c => c.id !== userData.id);
                    setSurvey({ ...data, id: surveySnap.id, candidates: filteredCandidates });
                }
            } catch (err) {
                console.error(err);
                setError("Erro ao carregar os dados da votação.");
            } finally {
                setLoading(false);
            }
        };

        if (userData) fetchSurvey();
    }, [surveyId, userData]);

    const handleVoteSubmit = async () => {
        if (!selectedCandidate) return;
        setIsSubmitting(true);

        try {
            const surveyRef = doc(db, 'Empresas', userData.empresa_id, 'employeeOfMonth', survey.id);
            
            // 1. Registra o voto anônimo (ou nominal, dependendo da sua regra) na subcoleção
            await addDoc(collection(surveyRef, 'votes'), {
                candidateId: selectedCandidate.id,
                voterId: userData.id, // Guardamos para auditoria se necessário
                timestamp: serverTimestamp()
            });

            // 2. Adiciona o usuário na lista de "já votou" para bloquear duplo voto
            await updateDoc(surveyRef, {
                votedUsers: arrayUnion(userData.id)
            });

            setHasVoted(true);
        } catch (err) {
            console.error("Erro ao registrar voto", err);
            alert("Erro ao registrar seu voto. Tente novamente.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) return <div className="loading-container"><div className="spinner"></div></div>;

    if (error) return (
        <div className="voting-error-container">
            <FaExclamationTriangle className="error-icon" />
            <h2>Acesso Negado</h2>
            <p>{error}</p>
            <button className="btn-return" onClick={() => navigate('/colaborador-do-mes')}>Voltar ao Início</button>
        </div>
    );

    if (hasVoted) return (
        <div className="voting-success-container">
            <div className="success-animation"><FaCheckCircle /></div>
            <h2>Voto Registrado com Sucesso!</h2>
            <p>Obrigado por participar. Os resultados serão divulgados em breve pelo time de People.</p>
            <button className="btn-return" onClick={() => navigate('/colaborador-do-mes')}>Voltar ao Início</button>
        </div>
    );

    return (
        <div className="voting-page-container">
            <header className="voting-header">
                <FaTrophy className="header-trophy" />
                <h1>{survey.title}</h1>
                <p>Selecione o colaborador que mais se destacou neste período.</p>
                <div className="security-badge"><FaLock /> Seu voto é confidencial</div>
            </header>

            <div className="candidates-grid">
                {survey.candidates.map(candidate => (
                    <div 
                        key={candidate.id} 
                        className={`candidate-card ${selectedCandidate?.id === candidate.id ? 'selected' : ''}`}
                        onClick={() => setSelectedCandidate(candidate)}
                    >
                        <div className="candidate-photo">
                            {candidate.photoURL ? (
                                <img src={candidate.photoURL} alt={candidate.name} />
                            ) : (
                                <span>{candidate.name.charAt(0)}</span>
                            )}
                            {selectedCandidate?.id === candidate.id && <div className="selection-overlay"><FaCheckCircle /></div>}
                        </div>
                        <h3>{candidate.name}</h3>
                        <span className="candidate-dept">{candidate.departamento || candidate.loja}</span>
                    </div>
                ))}
            </div>

            <div className="voting-action-bar">
                <button 
                    className="btn-confirm-vote" 
                    disabled={!selectedCandidate || isSubmitting}
                    onClick={handleVoteSubmit}
                >
                    {isSubmitting ? 'Registrando...' : selectedCandidate ? `Confirmar voto em ${selectedCandidate.name.split(' ')[0]}` : 'Selecione um candidato'}
                </button>
            </div>
        </div>
    );
};

export default VotarColaborador;